import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { ResizableDocumentTree } from '../../components/Documents/ResizableDocumentTree';
import DocumentContent from '../../components/Documents/DocumentContent';
import { DocumentEditor } from '../../components/Documents/DocumentEditor';
import NewDocumentModal from '../../components/Documents/NewDocumentModal';
import { NewFolderModal } from '../../components/Documents/modals/NewFolderModal';
import TemplateSelectionModal from '../../components/Documents/TemplateSelectionModal';
import { AIContentGenerationModal } from '../../components/Documents/modals/AIContentGenerationModal';
import { UploadModal } from '../../components/Documents/modals/UploadModal';
import DiscardConfirmationModal from '../../components/Documents/DiscardConfirmationModal';
import { documentService, type CreateDocumentWorkflowOptions } from '../../services/documentService';
import { documentWorkflowService } from '../../services/documentWorkflowService';
import {
  WorkflowSuggestionModal,
  type WorkflowSuggestionApplyResult,
} from '../../components/WorkflowSuggestionModal';
import { templateService } from '../../services/templateService';
import { DocumentTreeExpandSignal } from '../../components/Documents/DocumentTree';
import { DocumentNode, Document, DocumentDto } from '../../types/documents';
import { applyRenameToTree, parentPathFromDocumentPath, remapDescendantPaths } from '../../utils/documentTreeRename';
import { apiErrorMessage } from '../../utils/apiErrorMessage';
import { Template } from '../../types/templates';
import { PlusIcon, FolderPlusIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { useToast } from '../../components/UI/ToastProvider';
import { useDocumentPermissions } from '../../hooks/useDocumentPermissions';
import { PermissionLoader } from '../../components/UI/PermissionLoader';
import { ActionBlockedModal } from '../../components/UI/ActionBlockedModal';
import { useAuth } from '../../contexts/AuthContext';
import SearchableSelect from '../../components/UI/SearchableSelect';
import { useCrossTenantSelector, getCustomerIdFromStorage } from '../../hooks/useCrossTenantSelector';

interface WorkflowConfig {
  workflowId: string;
  versioningType: 'manual' | 'automatic';
  publishDate: string;
  archiveDate: string;
  relatedDocuments: string[];
  stepApprovers: Record<string, string[]>;
}

function fileExtensionForNewDocument(
  document: Document,
  templateId: string | null | undefined
): string | undefined {
  if (templateId) return undefined;
  const name = document.attachments?.[0]?.name;
  if (name) {
    const m = name.toLowerCase().match(/(\.[a-z0-9]+)$/);
    if (m) return m[1];
  }
  return '.edoc';
}

function newDocumentWorkflowOptions(
  result: WorkflowSuggestionApplyResult,
  templateId: string | null,
  fileExtension: string | undefined
): CreateDocumentWorkflowOptions {
  const base = {
    templateId: templateId || undefined,
    fileExtension: fileExtension || undefined,
    requiresSignature: result.requiresSignature || undefined,
    signers: result.signers,
    signatureExpiresAt: result.signatureExpiresAt ?? undefined,
  };

  if (result.skipWorkflow) {
    return { ...base, skipWorkflow: true };
  }

  return {
    ...base,
    skipWorkflow: false,
    manualWorkflowId: result.manualWorkflowId,
  };
}

const DocumentsPage: React.FC = () => {
  const location = useLocation();
  const selectFolderNavigationGen = useRef(0);

  const [selectedNode, setSelectedNode] = useState<DocumentNode | null>(null);
  const [treeData, setTreeData] = useState<DocumentNode | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [showNewDocumentModal, setShowNewDocumentModal] = useState(false);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showAIContentModal, setShowAIContentModal] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | undefined>(undefined);
  /** Alinhado ao Publicar legacy do DocumentContent: false se o documento já tem workflow. */
  const [editorAllowDirectPublish, setEditorAllowDirectPublish] = useState(true);
  const [documentId, setDocumentId] = useState<string | null>(null); // Variável para armazenar o ID do documento (GUID)
  const [notifications, setNotifications] = useState<{ id: string; message: string; type: 'success' | 'error' }[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const { addToast } = useToast();
  const { user } = useAuth();
  const [nodeToDelete, setNodeToDelete] = useState<DocumentNode | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSavingDocument, setIsSavingDocument] = useState(false);
  const [isPublishingDocument, setIsPublishingDocument] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [newDocumentTemplateId, setNewDocumentTemplateId] = useState<string | null>(null);
  const [showNewDocumentWorkflowModal, setShowNewDocumentWorkflowModal] = useState(false);
  const [newDocWorkflowPending, setNewDocWorkflowPending] = useState<{
    action: 'save' | 'publish';
    document: Document;
  } | null>(null);
  const [treeExpandSignal, setTreeExpandSignal] = useState<DocumentTreeExpandSignal>({
    version: 0,
    folderIds: [],
  });

  // Estados para modal de ação bloqueada
  const [showActionBlockedModal, setShowActionBlockedModal] = useState(false);
  const [blockedActionName, setBlockedActionName] = useState<string>('');

  // Hook para gerenciar permissões (usando o documento selecionado como referência)
  const { permissions, isLoading: permissionsLoading } = useDocumentPermissions(selectedNode?.id || null);

  // Função auxiliar para normalizar countChild
  const normalizeCountChild = useCallback((node: any): any => {
    return {
      ...node,
      countChild: node.countChild ?? 0,
      children: node.children ? node.children.map(normalizeCountChild) : [],
    };
  }, []);

  const handleNodeRenamed = useCallback(
    (dto: DocumentDto) => {
      const id = dto.id;
      if (!id) return;
      setTreeData((prev) => {
        if (!prev) return prev;
        return normalizeCountChild(applyRenameToTree(prev, id, dto));
      });
      setSelectedNode((prev) => {
        if (!prev || prev.id !== id) return prev;
        const oldPath = prev.path;
        const newPath = dto.path;
        const merged: DocumentNode = {
          ...prev,
          title: dto.title,
          path: dto.path,
          fullPath: dto.fullPath ?? prev.fullPath,
        };
        if (oldPath !== newPath && prev.children?.length) {
          return {
            ...merged,
            children: prev.children.map((ch) => remapDescendantPaths(ch, oldPath, newPath)),
          };
        }
        return merged;
      });
    },
    [normalizeCountChild]
  );

  const handleAfterDuplicate = useCallback(
    async (dto: DocumentDto) => {
      const id = dto.id;
      if (!id) {
        addToast('Resposta inválida da API.', 'error');
        return;
      }
      const parentPath = parentPathFromDocumentPath(dto.path || '');
      try {
        const updatedParent = await documentService.getDocumentWithChildren(parentPath);
        setTreeData((prev) => {
          if (!prev) return prev;
          const updateNode = (n: DocumentNode): DocumentNode => {
            if (n.path === parentPath) {
              return {
                ...n,
                children: updatedParent.children || [],
                countChild: updatedParent.countChild || 0,
              };
            }
            if (n.children) {
              return { ...n, children: n.children.map(updateNode) };
            }
            return n;
          };
          return normalizeCountChild(updateNode(prev));
        });
        const newNode = (updatedParent.children || []).find((c) => c.id === id);
        if (newNode) {
          setSelectedNode(newNode);
        }
        if (dto.parentDocumentId) {
          setTreeExpandSignal((s) => ({
            version: s.version + 1,
            folderIds: [dto.parentDocumentId!],
          }));
        }
        addToast('Documento duplicado com sucesso.', 'success');
      } catch {
        addToast(
          'Não foi possível atualizar a árvore. Recarregue a página se o item não aparecer.',
          'error'
        );
      }
    },
    [addToast, normalizeCountChild]
  );

  const duplicateDocumentFlow = useCallback(
    async (documentId: string) => {
      setIsDuplicating(true);
      try {
        const dto = await documentService.duplicateDocument(documentId);
        await handleAfterDuplicate(dto);
      } catch (error: unknown) {
        const ax = error as { response?: { status?: number; data?: { message?: string } } };
        const status = ax.response?.status;
        const msg = ax.response?.data?.message;
        if (status === 400) {
          addToast(msg || 'Não foi possível duplicar o documento.', 'error');
        } else if (status === 403) {
          addToast(msg || 'Acesso negado.', 'error');
        } else if (status === 404) {
          addToast(msg || 'Documento não encontrado.', 'error');
        } else if (msg) {
          addToast(msg, 'error');
        } else {
          addToast('Não foi possível duplicar o documento.', 'error');
        }
      } finally {
        setIsDuplicating(false);
      }
    },
    [addToast, handleAfterDuplicate]
  );

  // Monitorar mudanças nos estados do modal de exclusão
  useEffect(() => {
    // Estado do modal de exclusão mudou
  }, [showDeleteModal, nodeToDelete]);

  // Limpar estado quando o componente for desmontado
  useEffect(() => {
    return () => {
      setShowDeleteModal(false);
      setNodeToDelete(null);
    };
  }, []);

  useEffect(() => {
    // Verificar se o usuário está autenticado
    const userToken = sessionStorage.getItem('@KnowInDoc:accessToken');
    
    if (!userToken) {
      addToast('Usuário não autenticado. Faça login novamente.', 'error');
      // Redirecionar para login após 2 segundos
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
      return;
    }

    documentService.getDocumentWithChildren('/')
      .then(data => {
        setTreeData(normalizeCountChild(data));
      })
      .catch(err => {
        console.error('Erro ao carregar documentos:', err);
        if (err.response?.status === 401) {
          addToast('Sessão expirada. Redirecionando para login...', 'error');
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
        } else {
          addToast('Erro ao carregar documentos', 'error');
        }
      })
      .finally(() => setIsLoading(false));
  }, [normalizeCountChild]);

  const loadDocuments = useCallback(async () => {
    try {
      const data = await documentService.getDocumentWithChildren('/');
      setTreeData(normalizeCountChild(data));
    } catch (error) {
      addToast('Erro ao carregar documentos', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [addToast, normalizeCountChild]);

  const {
    canSelectTenant,
    tenants,
    tenantOptions,
    selectedTenantId,
    handleTenantChange,
    tenantsLoading,
    currentCustomerId,
    isManagingOtherTenant,
  } = useCrossTenantSelector(async tenantId => {
    documentService.setActiveTenantId(tenantId === getCustomerIdFromStorage() ? null : tenantId);
    setSelectedNode(null);
    setIsLoading(true);
    await loadDocuments();
  });

  const effectiveCustomerId = selectedTenantId || currentCustomerId;
  const selectedTenantName = tenants.find(t => t.id === selectedTenantId)?.name;

  useEffect(() => {
    return () => {
      documentService.setActiveTenantId(null);
    };
  }, []);

  // Função para buscar filhos de uma pasta e anexar ao nó correspondente
  const onExpandNode = async (node: DocumentNode) => {
    try {
      let data: DocumentNode;
      
      // Usar sempre getDocumentWithChildren para consistência
      data = await documentService.getDocumentWithChildren(node.path);
      
      // Atualiza a árvore anexando os filhos ao nó expandido
      setTreeData(prev => {
        if (!prev) return prev;
        const updateNode = (n: DocumentNode): DocumentNode => {
          if (n.id === node.id) {
            // Atualiza apenas os filhos e o countChild
            return { 
              ...n, 
              children: data.children || [],
              countChild: data.countChild || 0
            };
          }
          if (n.children) {
            return { ...n, children: n.children.map(updateNode) };
          }
          return n;
        };
        return normalizeCountChild(updateNode(prev));
      });
    } catch (error) {
      console.error('Erro em onExpandNode:', error);
      addToast('Erro ao carregar subpastas', 'error');
    }
  };

  const showNotification = (message: string, type: 'success' | 'error') => {
    const id = Date.now().toString();
    setNotifications((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 5000);
  };

  const applyNodeSelection = useCallback(async (node: DocumentNode) => {
    setSelectedNode(node);

    if (node.nodeType === 'folder' && node.countChild > 0) {
      try {
        const data = await documentService.getDocumentWithChildren(node.path);

        setSelectedNode({
          ...node,
          children: data.children || [],
          countChild: data.countChild || 0,
        });

        setTreeData((prev) => {
          if (!prev) return prev;
          const updateNode = (n: DocumentNode): DocumentNode => {
            if (n.id === node.id) {
              return {
                ...n,
                children: data.children || [],
                countChild: data.countChild || 0,
              };
            }
            if (n.children) {
              return { ...n, children: n.children.map(updateNode) };
            }
            return n;
          };
          return updateNode(prev);
        });
      } catch (error) {
        console.error('Erro ao carregar filhos da pasta:', error);
        addToast('Erro ao carregar conteúdo da pasta', 'error');
      }
    }
  }, [addToast]);

  const handleNodeSelect = async (node: DocumentNode) => {
    await applyNodeSelection(node);
  };

  useEffect(() => {
    if (isLoading) return;

    const selectFolderPath = (location.state as { selectFolderPath?: string } | undefined)?.selectFolderPath;
    if (selectFolderPath === undefined) return;

    const myGen = ++selectFolderNavigationGen.current;
    void (async () => {
      try {
        const data = await documentService.getDocumentWithChildren(selectFolderPath);
        if (myGen !== selectFolderNavigationGen.current) return;
        await applyNodeSelection(data);
      } catch (error) {
        if (myGen !== selectFolderNavigationGen.current) return;
        console.error('Erro ao abrir pasta a partir da navegação:', error);
        addToast('Erro ao abrir pasta', 'error');
      }
    })();
  }, [isLoading, location.key, location.state, applyNodeSelection, addToast]);

  const handleNewDocument = () => {
    setShowNewDocumentModal(true);
  };

  const handleUploadDocument = () => {
    setShowUploadModal(true);
  };

  const handleNewFolder = () => {
    setShowNewFolderModal(true);
  };

  const handleDocumentTypeSelect = (type: 'electronic' | 'upload' | 'ai', path?: string) => {
    // Fechar a modal de seleção do tipo de documento
    setShowNewDocumentModal(false);
    
    if (type === 'electronic') {
      loadTemplates();
      setShowTemplateModal(true);
      // Armazenar o path selecionado para usar na criação do documento
      if (path && selectedNode) {
        setSelectedNode({ ...selectedNode, path });
      } else if (path) {
        // Se não há selectedNode mas temos um path, criar um nó temporário
        setSelectedNode({
          id: 'temp',
          customerId: '',
          title: '',
          description: '',
          nodeType: 'folder',
          parentDocumentId: null,
          level: 0,
          order: 0,
          children: [],
          countChild: 0,
          path,
          fullPath: path,
          status: '',
          currentVersion: undefined,
          content: undefined
        });
      }
    } else if (type === 'ai') {
      // Abrir modal de criação com IA
      setShowAIContentModal(true);
      // Armazenar o path selecionado para usar na criação do documento
      if (path && selectedNode) {
        setSelectedNode({ ...selectedNode, path });
      } else if (path) {
        // Se não há selectedNode mas temos um path, criar um nó temporário
        setSelectedNode({
          id: 'temp',
          customerId: '',
          title: '',
          description: '',
          nodeType: 'folder',
          parentDocumentId: null,
          level: 0,
          order: 0,
          children: [],
          countChild: 0,
          path,
          fullPath: path,
          status: '',
          currentVersion: undefined,
          content: undefined
        });
      }
    } else if (type === 'upload') {
      // Abrir modal de upload
      setShowUploadModal(true);
      // Armazenar o path selecionado para usar no upload
      if (path && selectedNode) {
        setSelectedNode({ ...selectedNode, path });
      } else if (path) {
        // Se não há selectedNode mas temos um path, criar um nó temporário
        setSelectedNode({
          id: 'temp',
          customerId: '',
          title: '',
          description: '',
          nodeType: 'folder',
          parentDocumentId: null,
          level: 0,
          order: 0,
          children: [],
          countChild: 0,
          path,
          fullPath: path,
          status: '',
          currentVersion: undefined,
          content: undefined
        });
      }
    }
  };

  const handleAIContentGenerated = (content: string) => {
    setNewDocumentTemplateId(null);
    setShowAIContentModal(false);
    
    // Criar um novo documento com o conteúdo gerado pela IA
    const newDocument: Document = {
      id: '',
      title: 'Documento Gerado por IA',
      description: 'Documento criado automaticamente com conteúdo gerado por IA',
      content,
      type: 'document',
      status: 'edit', // Status inicial como edit
      author: 0,
      team: 0,
      category: 0,
      tags: [],
      attachments: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: 1.0,
      publicationExpiresAt: null,
      isPublic: false
    };
    
    setSelectedDocument(newDocument);
    setDocumentId(null); // Reset do ID do documento
    setEditorAllowDirectPublish(true);
    setShowEditor(true);
  };

  const loadTemplates = async () => {
    try {
      setIsLoadingTemplates(true);
      const templates = await templateService.getTemplates(true); // includeEmpty=true
      setTemplates(templates);
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
      addToast('Erro ao carregar modelos.', 'error');
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  const handleTemplateSelect = (template: Template) => {
    setNewDocumentTemplateId(template.id);
    setShowTemplateModal(false);
    setShowNewDocumentModal(false);
    const newDocument: Document = {
      id: '', // ID 0 indica novo documento
      title: `Novo ${template.name}`,
      content: template.content || '',    
      type: 'document',
      status: 'edit', // Status inicial como edit
      author: 0,
      team: 0,
      category: 0,
      tags: [],
      attachments: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: 1,
      publicationExpiresAt: null,
      isPublic: false
    };
    setSelectedDocument(newDocument);
    setDocumentId(null); // Reset do ID do documento
    setEditorAllowDirectPublish(true);
    setShowEditor(true);
  };

  // Função para atualizar o selectedNode com os dados mais recentes
  const updateSelectedNode = async (documentId: string, newStatus?: string) => {
    try {
      
      if (!selectedNode) {
        return;
      }
      
      // Se o selectedNode é o próprio documento que foi atualizado
      if (selectedNode.id === documentId) {
        
        // Buscar o documento atualizado da API para garantir sincronização
        try {
          const updatedDocumentDetails = await documentService.getDocumentDetails(documentId);
          if (updatedDocumentDetails) {
            
            // Atualizar o selectedNode com os dados mais recentes
            const updatedSelectedNode: DocumentNode = {
              ...selectedNode,
              status: newStatus || updatedDocumentDetails.status || selectedNode.status,
              title: updatedDocumentDetails.title || selectedNode.title,
              description: updatedDocumentDetails.description || selectedNode.description,
              currentVersion:
                updatedDocumentDetails.currentVersion ?? selectedNode.currentVersion
            };
            
            setSelectedNode(updatedSelectedNode);
          } else {
            // Fallback: atualizar apenas o status se não conseguir buscar da API
            const updatedSelectedNode: DocumentNode = {
              ...selectedNode,
              status: newStatus || selectedNode.status
            };
            
            setSelectedNode(updatedSelectedNode);
          }
        } catch (apiError) {
          console.error('Erro ao buscar documento atualizado da API:', apiError);
          
          // Fallback: atualizar apenas o status
          const updatedSelectedNode: DocumentNode = {
            ...selectedNode,
            status: newStatus || selectedNode.status
          };
          
          setSelectedNode(updatedSelectedNode);
        }
      }
      
      // Também atualizar o nó na árvore para manter sincronização
      await updateSpecificNode(documentId);
      
    } catch (error) {
      console.error('Erro ao atualizar selectedNode:', error);
    }
  };

  const runCreateNewWithWorkflow = async (
    baseDocument: Document,
    result: WorkflowSuggestionApplyResult,
    action: 'save' | 'publish'
  ) => {
    const ext = fileExtensionForNewDocument(baseDocument, newDocumentTemplateId);
    const opts = newDocumentWorkflowOptions(result, newDocumentTemplateId, ext);
    const toPost =
      action === 'publish' ? { ...baseDocument, status: 'published' as const } : baseDocument;
    return documentService.createDocument(toPost, selectedNode?.path, opts);
  };

  const handleNewDocumentWorkflowConfirm = async (result: WorkflowSuggestionApplyResult) => {
    if (!newDocWorkflowPending) return;
    const { action, document } = newDocWorkflowPending;
    setNewDocWorkflowPending(null);
    setShowNewDocumentWorkflowModal(false);
    if (action === 'save') {
      setIsSavingDocument(true);
    } else {
      setIsPublishingDocument(true);
    }
    try {
      const savedDocument = await runCreateNewWithWorkflow(document, result, action);
      if (action === 'save') {
        if (selectedNode && selectedNode.nodeType === 'folder') {
          await handleFolderCreated(selectedNode.path);
        } else {
          await handleFolderCreated('/');
        }
        addToast('Documento salvo com sucesso!', 'success');
        setShowEditor(false);
        setSelectedDocument(undefined);
        setDocumentId(null);
        setNewDocumentTemplateId(null);
      } else {
        if (selectedNode && selectedNode.id === 'temp') {
          setSelectedNode({
            ...selectedNode,
            id: savedDocument.id.toString(),
            status: 'published',
          });
        }
        if (selectedNode && selectedNode.nodeType === 'folder') {
          await handleFolderCreated(selectedNode.path);
        } else {
          await handleFolderCreated('/');
        }
        addToast('Documento publicado com sucesso!', 'success');
        setShowEditor(false);
        setSelectedDocument(undefined);
        setDocumentId(null);
        setNewDocumentTemplateId(null);
        loadDocuments();
      }
    } catch (error) {
      console.error('Erro ao criar documento:', error);
      const fallback = action === 'save'
        ? 'Erro ao salvar documento. Tente novamente.'
        : 'Erro ao publicar documento. Tente novamente.';
      addToast(apiErrorMessage(error, fallback), 'error');
    } finally {
      if (action === 'save') {
        setIsSavingDocument(false);
      } else {
        setIsPublishingDocument(false);
      }
    }
  };

  const handleSaveDocument = async (document: Document) => {
    if (documentId === null) {
      if (effectiveCustomerId) {
        setNewDocWorkflowPending({ action: 'save', document });
        setShowNewDocumentWorkflowModal(true);
        return;
      }
    }

    setIsSavingDocument(true);
    try {
      let savedDocument: Document;
      if (documentId === null) {
        savedDocument = await documentService.createDocument(document, selectedNode?.path);
        if (selectedNode && selectedNode.nodeType === 'folder') {
          await handleFolderCreated(selectedNode.path);
        } else {
          await handleFolderCreated('/');
        }
      } else {
        savedDocument = await documentService.updateDocument(documentId!, document);
        await updateSelectedNode(savedDocument.id.toString(), document.status || 'edit');
        await updateSpecificNode(savedDocument.id.toString());
      }

      addToast('Documento salvo com sucesso!', 'success');
      setShowEditor(false);
      setSelectedDocument(undefined);
      setDocumentId(null);
    } catch (error) {
      console.error('Erro ao salvar documento:', error);
      addToast(apiErrorMessage(error, 'Erro ao salvar documento. Tente novamente.'), 'error');
    } finally {
      setIsSavingDocument(false);
    }
  };

  const handleDiscardDocument = async () => {
    try {
      // TODO: Implementar chamada à API
      addToast('Documento descartado com sucesso', 'success');
      setNewDocumentTemplateId(null);
      setShowDiscardModal(false);
      setShowEditor(false);
      setDocumentId(null); // Reset do documentId
      loadDocuments();
    } catch (error) {
      addToast('Erro ao descartar mudança do documento', 'error');
    }
  };

  const handlePublishDocument = async (document: Document) => {
    setIsPublishingDocument(true);
    try {
      if (document.id) {
        const updateData: Partial<Document> = {
          title: document.title,
          description: document.description,
          content: document.content,
          coverImage: document.coverImage,
          attachments: document.attachments,
          updatedAt: new Date().toISOString()
        };

        await documentService.updateDocument(document.id, updateData);
        await documentService.publishDocument(document.id, {
          publicationExpiresAt: document.publicationExpiresAt ?? null,
        });
        await updateSelectedNode(document.id.toString(), 'published');
        await updateSpecificNode(document.id.toString());
      } else {
        const ext = fileExtensionForNewDocument(document, newDocumentTemplateId);
        const savedDocument = await documentService.createDocument(
          { ...document, status: 'published' },
          selectedNode?.path,
          {
            skipWorkflow: true,
            templateId: newDocumentTemplateId || undefined,
            fileExtension: ext || undefined,
          }
        );
        if (selectedNode && selectedNode.id === 'temp') {
          setSelectedNode({
            ...selectedNode,
            id: savedDocument.id.toString(),
            status: 'published'
          });
        }
        if (selectedNode && selectedNode.nodeType === 'folder') {
          await handleFolderCreated(selectedNode.path);
        } else {
          await handleFolderCreated('/');
        }
      }
      
      addToast('Documento publicado com sucesso!', 'success');
      setShowEditor(false);
      setSelectedDocument(undefined);
      setDocumentId(null);
      loadDocuments();
    } catch (error) {
      console.error('Erro ao publicar documento:', error);
      addToast(apiErrorMessage(error, 'Erro ao publicar documento. Tente novamente.'), 'error');
    } finally {
      setIsPublishingDocument(false);
    }
  };

  const handlePublishFromDetail = async (
    node: DocumentNode,
    options?: { publicationExpiresAt?: string | null }
  ) => {
    await documentService.publishDocument(node.id, {
      publicationExpiresAt: options?.publicationExpiresAt ?? null,
    });
    await updateSelectedNode(node.id, 'published');
    await updateSpecificNode(node.id);
    loadDocuments();
  };

  const handleWorkflowConfig = async (config: WorkflowConfig) => {
    try {
      // TODO: Implementar chamada à API
      addToast('Documento publicado com sucesso', 'success');
      loadDocuments();
    } catch (error) {
      addToast('Erro ao publicar documento', 'error');
    }
  };

  const handleOpenDocument = async (node: DocumentNode) => {
    if ((node.nodeType === 'document' || node.nodeType === 'edoc') && node.metadata) {
      try {
        // Buscar o documento completo da API usando /documents/{id}/details
        const [documentDetails, documentWorkflow] = await Promise.all([
          documentService.getDocumentDetails(node.id),
          documentWorkflowService.getDocumentWorkflow(node.id),
        ]);

        // Mesma regra do Publicar legacy em DocumentContent: só direto se não há workflow.
        setEditorAllowDirectPublish(!documentWorkflow);
        
        if (documentDetails) {
          // Converter DocumentDetailsDto para Document
          const document: Document = {
            id: documentDetails.id || node.id,
            title: documentDetails.title,
            description: documentDetails.description || '',
            content: documentDetails.content?.content || '',
            coverImage: documentDetails.cover?.url || undefined, // Usar a URL da API
            type: 'document',
            status: documentDetails.status as Document['status'],
            author: 0,
            team: 0,
            category: 0,
            tags: [],
            attachments: documentDetails.attachments.map(attachment => ({
              id: attachment.id,
              name: attachment.fileName,
              size: attachment.fileSize,
              type: attachment.contentType,
              url: attachment.url || attachment.storagePath, // Usar a URL da API se disponível, senão storagePath
              uploadedAt: new Date(),
              uploadedBy: 'Usuário Atual'
            })),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            version: Number(documentDetails.currentVersion) || 1,
            publicationExpiresAt: documentDetails.publicationExpiresAt ?? null,
            isPublic: false
          };
          
          // Atualizar imediatamente o selectedNode com o status correto da API
          if (selectedNode && selectedNode.id === node.id) {
            const updatedSelectedNode: DocumentNode = {
              ...selectedNode,
              status: documentDetails.status || selectedNode.status,
              title: documentDetails.title || selectedNode.title,
              description: documentDetails.description || selectedNode.description,
              currentVersion: documentDetails.currentVersion ?? selectedNode.currentVersion
            };
            setSelectedNode(updatedSelectedNode);
          }
          
          setSelectedDocument(document);
          setDocumentId(node.id);         
          setShowEditor(true);
        } else {
          // Fallback para os dados básicos do nó se a API não retornar o documento completo
          const document: Document = {
            id: node.id,
            title: node.title,
            description: node.description || '',
            content: node.metadata.content || '',
            coverImage: undefined,
            type: 'document',
            status: node.status as Document['status'],
            author: 0,
            team: 0,
            category: 0,
            tags: [],
            attachments: [],
            createdAt: node.metadata.createdAt || new Date().toISOString(),
            updatedAt: node.metadata.lastModifiedAt || node.metadata.updatedAt || new Date().toISOString(),
            version: 1,
            publicationExpiresAt: null,
            isPublic: false
          };
          setSelectedDocument(document);
          setDocumentId(node.id);

          setShowEditor(true);
        }
      } catch (error) {
        console.error('Erro ao carregar documento completo:', error);
        addToast('Erro ao carregar documento. Tente novamente.', 'error');
        
        // Fallback: sem confirmar workflow, não liberar publicação direta.
        setEditorAllowDirectPublish(false);
        const document: Document = {
          id: node.id,
          title: node.title,
          description: node.description || '',
          content: node.metadata.content || '',
          coverImage: undefined,
          type: 'document',
          status: 'edit',
          author: 0,
          team: 0,
          category: 0,
          tags: [],
          attachments: [],
          createdAt: node.metadata.createdAt || new Date().toISOString(),
          updatedAt: node.metadata.lastModifiedAt || node.metadata.updatedAt || new Date().toISOString(),
          version: 1,
          publicationExpiresAt: null,
          isPublic: false
        };
        setSelectedDocument(document);
        setDocumentId(node.id);
        setShowEditor(true);
      }
    }
  };

  const handleEditDocument = (node: DocumentNode) => {
    
    // Atualizar imediatamente o status para 'edit' quando entrar em modo de edição
    if (selectedNode && selectedNode.id === node.id) {
      const updatedSelectedNode: DocumentNode = {
        ...selectedNode,
        status: 'edit'
      };
      setSelectedNode(updatedSelectedNode);
    }
    
    // Reutiliza a mesma lógica do handleOpenDocument
    handleOpenDocument(node);
  };


  // Funções para mostrar modal de ação bloqueada
  const showActionBlocked = (actionName: string) => {
    setBlockedActionName(actionName);
    setShowActionBlockedModal(true);
  };

  const handleDeleteDocument = (node: DocumentNode) => {
    setNodeToDelete(node);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!nodeToDelete) return;
    try {
      await documentService.deleteNode(nodeToDelete.id);
      addToast('Excluído com sucesso!', 'success');
      setShowDeleteModal(false);
      
      // Limpar seleção se o documento excluído estava selecionado
      if (selectedNode?.id === nodeToDelete.id) {
        setSelectedNode(null);
      }
      
      // Usar a nova função para atualizar a árvore
      await handleDocumentDeleted(nodeToDelete);
      
      setNodeToDelete(null);
    } catch (err: any) {
      addToast('Erro ao excluir: ' + (err?.response?.data?.message || err.message || 'Erro desconhecido'), 'error');
    }
  };

  // Função auxiliar para encontrar o path pelo id
  function findPathById(node: DocumentNode, id: string): string | null {
    if (node.id === id) return node.path;
    for (const child of node.children) {
      const result = findPathById(child, id);
      if (result) return result;
    }
    return null;
  }

  // Atualiza a árvore após criar uma nova pasta
  const handleFolderCreated = async (path: string) => {
    if (!path) return;
    try {
      let updatedParent: DocumentNode;
      
      // Usar sempre getDocumentWithChildren para consistência
      updatedParent = await documentService.getDocumentWithChildren(path);
      
      setTreeData(prev => {
        if (!prev) return prev;
        // Função recursiva para atualizar apenas o nó pai
        const updateNode = (node: DocumentNode): DocumentNode => {
          if (node.path === path) {
            // Atualiza apenas os filhos e countChild, mantém o resto do nó
            return { 
              ...node, 
              children: updatedParent.children || [],
              countChild: updatedParent.countChild || 0
            };
          }
          if (node.children) {
            return { ...node, children: node.children.map(updateNode) };
          }
          return node;
        };
        return normalizeCountChild(updateNode(prev));
      });
      
      // Seleciona o nó pai atualizado
      setSelectedNode(prev => {
        if (prev && prev.path === path) {
          return { ...prev, children: updatedParent.children || [], countChild: updatedParent.countChild || 0 };
        }
        return prev;
      });
    } catch (err) {
      addToast('Erro ao atualizar a árvore após criar pasta', 'error');
    }
  };

  // Função para atualizar a árvore após exclusão de documento
  const handleDocumentDeleted = async (deletedNode: DocumentNode) => {
    try {
      // Remove o nó da árvore localmente primeiro
      setTreeData(prev => {
        if (!prev) return prev;
        const removeNode = (node: DocumentNode): DocumentNode | null => {
          if (node.id === deletedNode.id) {
            return null; // Remove o nó
          }
          if (node.children) {
            const filteredChildren = node.children
              .map(removeNode)
              .filter((child): child is DocumentNode => child !== null);
            return {
              ...node,
              children: filteredChildren,
              countChild: filteredChildren.length
            };
          }
          return node;
        };
        return normalizeCountChild(removeNode(prev) || prev);
      });

      // Atualiza o nó pai para sincronizar com o backend
      const parentPath = deletedNode.parentDocumentId && treeData ? findPathById(treeData, deletedNode.parentDocumentId) : '/';
      if (parentPath) {
        await handleFolderCreated(parentPath);
      }
    } catch (err) {
      console.error('Erro ao atualizar árvore após exclusão:', err);
      addToast('Erro ao atualizar a árvore após exclusão', 'error');
    }
  };

  // Atualiza apenas um nó específico na árvore
  const updateSpecificNode = async (nodeId: string) => {
    
    try {
      // Encontra o nó pai do documento adicionado
      const findParentNode = (node: DocumentNode): DocumentNode | null => {
        if (node.children && node.children.some(child => child.id === nodeId)) {
          return node;
        }
        if (node.children) {
          for (const child of node.children) {
            const result = findParentNode(child);
            if (result) return result;
          }
        }
        return null;
      };

      const parentNode = treeData ? findParentNode(treeData) : null;
      
      if (parentNode) {
        // Atualiza apenas o nó pai
        const updatedParent = await documentService.getDocumentWithChildren(parentNode.path);
        
        setTreeData(prev => {
          if (!prev) return prev;
          const updateNode = (n: DocumentNode): DocumentNode => {
            if (n.id === parentNode.id) {
              return { 
                ...n, 
                children: updatedParent.children || [],
                countChild: updatedParent.countChild || 0
              };
            }
            if (n.children) {
              return { ...n, children: n.children.map(updateNode) };
            }
            return n;
          };
          return updateNode(prev);
        });
      } else {
      }
    } catch (error) {
      console.error('Erro ao atualizar nó específico:', error);
      
      // Log mais detalhado do erro
      if (error instanceof Error) {
        console.error('Mensagem de erro:', error.message);
        console.error('Stack trace:', error.stack);
      }
      
      // Se for um erro de API, tentar extrair mais informações
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as any;
        console.error('Status da API:', apiError.response?.status);
        console.error('Dados da resposta:', apiError.response?.data);
      }
    }
  };

  return (
    <div className="flex-1">
      <div className="mb-6">
        <div>
          <h1 className="text-2xl font-bold mb-2">Documentos</h1>
          <p className="text-gray-600">Gerencie seus documentos com processamento inteligente da Baruk.</p>
        </div>

        <div className="mt-4 flex items-center justify-between gap-4">
          {/* Esquerda: seletor de cliente (apenas administrador do cliente proprietário) */}
          <div className="w-72 max-w-full">
            {canSelectTenant && (
              <SearchableSelect
                options={tenantOptions}
                value={selectedTenantId || null}
                onChange={handleTenantChange}
                placeholder="Selecione um cliente"
                searchPlaceholder="Buscar cliente..."
                emptyMessage="Nenhum cliente encontrado"
                disabled={tenantsLoading}
                ariaLabel="Selecionar cliente para visualizar a árvore de documentos"
              />
            )}
          </div>

          {/* Direita: botões de criação - apenas se tiver permissão */}
          <PermissionLoader isLoading={permissionsLoading}>
            {permissions.canCreate && (
              // Só mostra os botões se nenhum nó estiver selecionado OU se for uma pasta
              (!selectedNode || selectedNode.nodeType === 'folder') && (
                <div className="flex gap-2">
                  <button
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
                    onClick={() => setShowNewDocumentModal(true)}
                  >
                    <PlusIcon className="h-4 w-4" />
                    Novo Documento
                  </button>
                  <button
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2"
                    onClick={() => setShowNewFolderModal(true)}
                  >
                    <FolderPlusIcon className="h-4 w-4" />
                    Nova Pasta
                  </button>
                  <button
                    className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center gap-2"
                    onClick={() => setShowUploadModal(true)}
                  >
                    <ArrowUpTrayIcon className="h-4 w-4" />
                    Upload
                  </button>
                </div>
              )
            )}
          </PermissionLoader>
        </div>
      </div>

      {isManagingOtherTenant && (
        <div className="mb-4 flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <svg xmlns="http://www.w3.org/2000/svg" className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <span>
            Você está visualizando a árvore de documentos do cliente{' '}
            <strong>{selectedTenantName}</strong>. Alterações feitas aqui afetam o ambiente desse cliente.
          </span>
        </div>
      )}

      <div className="flex gap-6">
        {/* Árvore de documentos redimensionável */}
        <ResizableDocumentTree 
          treeData={treeData}
          onNodeSelect={handleNodeSelect}
          selectedNode={selectedNode}
          onExpandNode={onExpandNode}
          onDeleteNode={handleDeleteDocument}
          onEditDocument={handleEditDocument}
          onNodeRenamed={handleNodeRenamed}
          onNodeMoved={loadDocuments}
          onDuplicateDocument={duplicateDocumentFlow}
          treeExpandSignal={treeExpandSignal}
        />

        {/* Área de conteúdo */}
        <div className="flex-1 bg-white rounded-lg shadow-sm p-6">
          <DocumentContent
            selectedNode={selectedNode}
            contextTenantId={effectiveCustomerId}
            onNodeSelect={handleNodeSelect}
            onNewDocument={handleNewDocument}
            onNewFolder={handleNewFolder}
            onOpenDocument={handleOpenDocument}
            onDeleteDocument={handleDeleteDocument}
            onDocumentUpdated={async (documentId: string) => {
              await updateSelectedNode(documentId);
              await updateSpecificNode(documentId);
            }}
            onEditDocument={handleEditDocument}
            onDuplicateDocument={duplicateDocumentFlow}
            onPublishDocument={handlePublishFromDetail}
          />
        </div>
      </div>

      {/* Modais */}
      <NewDocumentModal
        isOpen={showNewDocumentModal}
        onClose={() => setShowNewDocumentModal(false)}
        onDocumentTypeSelect={handleDocumentTypeSelect}
        selectedPath={selectedNode?.path}
      />

      <TemplateSelectionModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        onTemplateSelect={handleTemplateSelect}
        templates={templates}
        isLoading={isLoadingTemplates}
      />

      <AIContentGenerationModal
        isOpen={showAIContentModal}
        onClose={() => setShowAIContentModal(false)}
        onContentGenerated={handleAIContentGenerated}
        documentType="documento"
      />

      {showEditor && (
        <DocumentEditor
          document={selectedDocument}
          onSave={handleSaveDocument}
          onPublish={handlePublishDocument}
          allowDirectPublish={editorAllowDirectPublish}
          isAdmin={user?.isAdmin === true}
          onDiscard={() => setShowDiscardModal(true)}
          onClose={() => {
            if (showNewDocumentWorkflowModal) return;
            setShowEditor(false);
            setDocumentId(null); // Reset do documentId ao fechar
          }}
          isSaving={isSavingDocument}
          isPublishing={isPublishingDocument}
        />
      )}

      <DiscardConfirmationModal
        isOpen={showDiscardModal}
        onClose={() => setShowDiscardModal(false)}
        onConfirm={handleDiscardDocument}
        collaborators={[]}
      />

      {/* Notificações */}
      <div className="fixed bottom-20 right-4 space-y-2 z-[60]">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`px-4 py-2 rounded-md shadow-lg ${
              notification.type === 'success'
                ? 'bg-green-500 text-white'
                : 'bg-red-500 text-white'
            }`}
          >
            {notification.message}
          </div>
        ))}
      </div>

      {isDuplicating && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg px-8 py-6 shadow-lg flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
            <p className="text-sm font-medium text-gray-700">Duplicando documento...</p>
            <p className="text-xs text-gray-500">Aguarde enquanto os arquivos são copiados.</p>
          </div>
        </div>
      )}

      {/* Modal de confirmação de exclusão */}
      {showDeleteModal && nodeToDelete && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full shadow-lg">
            <h2 className="text-lg font-semibold mb-4">Confirmar exclusão</h2>
            <p className="mb-6">Tem certeza que deseja excluir "{nodeToDelete.title}"?</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { 
                  setShowDeleteModal(false); 
                  setNodeToDelete(null); 
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  confirmDelete();
                }}
                className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      <NewFolderModal
        isOpen={showNewFolderModal}
        onClose={() => setShowNewFolderModal(false)}
        selectedPath={selectedNode?.path}
        onFolderCreated={handleFolderCreated}
      />

      <WorkflowSuggestionModal
        isOpen={showNewDocumentWorkflowModal}
        onClose={() => {
          setShowNewDocumentWorkflowModal(false);
          setNewDocWorkflowPending(null);
        }}
        customerId={effectiveCustomerId}
        templateId={newDocumentTemplateId || undefined}
        fileExtension={
          newDocWorkflowPending
            ? fileExtensionForNewDocument(newDocWorkflowPending.document, newDocumentTemplateId)
            : undefined
        }
        folderId={
          selectedNode?.nodeType === 'folder' &&
          selectedNode.id &&
          selectedNode.id !== 'temp' &&
          /^[0-9a-fA-F-]{36}$/.test(selectedNode.id)
            ? selectedNode.id
            : undefined
        }
        onConfirm={handleNewDocumentWorkflowConfirm}
      />

      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        selectedPath={selectedNode?.path}
        customerId={effectiveCustomerId || undefined}
        folderDocumentId={
          selectedNode?.nodeType === 'folder' &&
          selectedNode.id &&
          selectedNode.id !== 'temp' &&
          /^[0-9a-fA-F-]{36}$/.test(selectedNode.id)
            ? selectedNode.id
            : undefined
        }
        onDocumentUploaded={async () => {
          const folderPath = selectedNode?.path;
          if (folderPath)
          {
            await handleFolderCreated(folderPath);
          }
          else
          {
            await loadDocuments();
          }
          setShowUploadModal(false);
        }}
      />

      <ActionBlockedModal
        isOpen={showActionBlockedModal}
        onClose={() => setShowActionBlockedModal(false)}
        actionName={blockedActionName}
      />
    </div>
  );
};

export default DocumentsPage; 